import { Component } from 'react';
import 'react-table/react-table.css';
import NPReactTable from '../../NPReactTable';
import Select from 'react-select';
import { Col, Row, Form, FormGroup, Button, InputGroup } from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';
import ReactTooltip from 'react-tooltip';
import { formatDate, formatMarketAreaDesc } from '../AllocationHelper';
import constants from '../../util/constants';
import axios from 'axios';
import Dialog from 'react-bootstrap-dialog';
import * as Grants from '../../util/grants';
import { hasGrant } from '../../util/grantHelper';
import * as XLSX from 'xlsx';
import * as Convert from '../../util/unitConversion';
import './style.scss';
import { getBudgetWarningText } from '..';

// Constants - Fields available for grouping
const getPivotFields = (legalEntity) => {
  const fields = [
    { key: 'PM', label: 'PM' },
    { key: 'Country', label: 'Country' },
    { key: 'AreaSalesDirector', label: 'Market Area' },
    { key: 'Agent', label: 'Agent' },
    { key: 'Customer', label: 'Customer' },
    { key: 'Grade', label: 'Grade' }
  ];
  
  // Filter out Grade if legalEntity is NPBA
  if (legalEntity === 'NPBA') {
    return fields.filter(f => f.key !== 'Grade');
  }
  return fields;
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SUM_COL_WIDTH = 72;

const COLOR_WARNING = '#ff8888';
const COLOR_EDITED  = '#ffeeba';
const COLOR_NO_ALLOCATION_INFO = '#ffffff';
const COLOR_NO_MATCH = '#ffb366';

// Custom sort order for PM (based on mill). Values not in this list will be sorted last.
const PM_SORT_ORDER = [
  'PM4B',   // Bäckhammar
  'PM5B',   // Bäckhammar
  'PM1A',   // Åmotsfors
  'PM6A',   // Åmotsfors
  'PM01',   // Greåker
  'PM04',   // Greåker
  'PM02',   // Säffle
  'PM03',   // Säffle
];

// Used to indicate hovered rows in table
const darkenColor = (hex, percent = 10) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Darken by reducing each component
  r = Math.floor(r * (1 - percent / 100));
  g = Math.floor(g * (1 - percent / 100));
  b = Math.floor(b * (1 - percent / 100));
  
  // Convert back to hex
  const toHex = (c) => c.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

class AllocationGroups extends Component {
  constructor(props) {
    super(props);
    this.state = {
      header: this.props.apiToken ? {
                'Authorization': 'Bearer ' + this.props.apiToken,
                'Accept': 'application/octet-stream'
      } : {},
      dropdownStyleMedium: {
        valueContainer: (provided) => ({
          ...provided,
          minWidth: '100px'
        })
      },
      dropdownStyleSmall: {
        valueContainer: (provided) => ({
            ...provided,
            minWidth: '55px'
        }),
      },
      expanded: {},
      editingCell: null, // { allocationId, month, value }
      newEditedAllocations: null, // [{allocationId, month, allocated, originalAllocated, pm}],
      fieldValidationErrors: {}, // { `${allocationId}_${fieldKey}`: 'Error message' }
      groupEditingCell: null,
      showFullValues: false, // Toggle for display mode
      showCommentDialog: false,
      comment: "",
    };
    this.transformedDataRef = [];
  }

  formatNumber(value, shouldPad = false) {
    if (value == null || isNaN(value)){
      return '0';
    }
    const formatted = Number(value).toLocaleString('sv', {minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: true });
    return shouldPad ? formatted.padStart(7, '\u00A0') : formatted;
  }

  sortByFields = (data, fields) => {
    return [...data].sort((a, b) => {
      // Check if either item is a new booking (negative AllocationId)
      const aIsNew = a.AllocationId < 0;
      const bIsNew = b.AllocationId < 0;
      
      // New bookings should appear first
      if (aIsNew && !bIsNew) return -1;
      if (!aIsNew && bIsNew) return 1;
      
      // Check if either item has NoAllocationMatch
      const aHasNoMatch = a.NoAllocationMatch === true || a._leafRecords?.some(leaf => leaf.NoAllocationMatch === true);
      const bHasNoMatch = b.NoAllocationMatch === true || b._leafRecords?.some(leaf => leaf.NoAllocationMatch === true);
      
      // If one has NoAllocationMatch and the other doesn't, put the one with NoAllocationMatch last
      if (aHasNoMatch && !bHasNoMatch) return 1;
      if (!aHasNoMatch && bHasNoMatch) return -1;
      
      // Sort by fields in order
      for (let i = 0; i < fields.length; i++) {
        const field = typeof fields[i] === 'string' ? fields[i] : fields[i].key;
        const aVal = (a[field] || '').toString();
        const bVal = (b[field] || '').toString();
        
        // Apply custom sort order for PM field
        if (field === 'PM' && PM_SORT_ORDER.length > 0) {
          const aIndex = PM_SORT_ORDER.indexOf(aVal);
          const bIndex = PM_SORT_ORDER.indexOf(bVal);
          
          // Both values are in the custom sort order
          if (aIndex !== -1 && bIndex !== -1) {
            if (aIndex < bIndex) return -1;
            if (aIndex > bIndex) return 1;
          }
          // Only a is in the custom sort order (a comes first)
          else if (aIndex !== -1) {
            return -1;
          }
          // Only b is in the custom sort order (b comes first)
          else if (bIndex !== -1) {
            return 1;
          }
          // Neither is in custom sort order, fall through to alphabetical sort
        }
        
        // Default alphabetical sort
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
      }
      return 0;
    });
  };

  handleEditButtonClick = () => {
    this.setState({ editingCell: null });
    if (this.props.onEditModeChange) this.props.onEditModeChange(true);
  };

  handleCancelButtonClick = () => {
    if (this.props.onEditModeChange) this.props.onEditModeChange(false);
    window.location.reload();
  };

  saveEditedAllocations(editedAllocations, comment) {
    const { API_BASE_URL } = constants;
    return axios.post(
        `${API_BASE_URL}Allocation/SaveEditedAllocations`,
        editedAllocations,
        { 
          headers: this.state.header,
          params: { comment }
        }
    );
  }

  handleSaveButtonClick = () => {
    const canAddComment = hasGrant(Grants.TYPES.Allocation_AddAllocationComments);
    if (canAddComment){
      const actualEditedAllocations = this.props.editedAllocations.filter(x => x.allocated !== x.originalAllocated);
      // Show dialog for comment before saving
      this.dialog.show({
        title: "Save changes",
        body: (
          <div>
            <div>Please enter a comment for your changes:</div>
            <input
              type="text"
              className="form-control"
              ref={input => { this.commentInput = input; }}
              autoFocus
            />
            <em>You are about to save {actualEditedAllocations.length} months of allocated volume</em>
          </div>
        ),
        actions: [
          Dialog.CancelAction(),
          Dialog.Action('Save', () => {
            const comment = this.commentInput ? this.commentInput.value : '';
            this.saveEditedAllocations(actualEditedAllocations, comment)
              .then(() => { window.location.reload(); })
              .catch(() => {});
          }, 'btn-primary')
        ]
      });
    }
    else {
      this.saveEditedAllocations(this.props.editedAllocations, "")
        .then(() => { window.location.reload(); })
        .catch(() => {});
    }
  }

  handleAddAllocationToGroup = (groupRow, rowIndex) => {
    // Generate a negative allocationId for the new allocation to indicate new allocation
    const tempAllocationId = -Math.floor(Math.random() * 10000000);
    const newEditedAllocations = [
      ...this.props.editedAllocations,
      ...Array.from({ length: 12}, (_, i) => ({
        allocationId: tempAllocationId,
        month: i + 1,
        allocated: 0,
        originalAllocated: 0,
        pm: groupRow.PM || null,
        grade: groupRow.Grade || null,

        // These are not used for warnings but are used when onEditedAllocationsChange is called
        // and constructs a allocation leaf using these values
        agent: groupRow.Agent || null,
        areaSalesDirector: groupRow.AreaSalesDirector || null,
        country: groupRow.Country || null,
        customer: groupRow.Customer || null,
      }))
    ];

    // Expand the group if it's not already expanded
    const expanded = { ...this.state.expanded };
    if (rowIndex !== undefined && !expanded[rowIndex]) {
      expanded[rowIndex] = true;
    }

    // Update editedallocations to display warningmessages for new allocations
    // Warningmessages will not show if grade or pm is not set on new allocation
    this.setState({ newEditedAllocations, expanded });
    this.props.onEditedAllocationsChange(newEditedAllocations);
  }

  handleCellClick = (allocationId, month) => {
    if (!this.props.editMode) return;

    this.setState({ editingCell: { allocationId, month }});
  };

  handleCellValueChange = (e, originalAllocated, pm, grade, customer) => {
    const { editingCell } = this.state;
    const newAllocated = Number(e.target.value.replace(',', '.'));
    this.setState({ editingCell: {...editingCell, value: e.target.value} });

    let newEditedAllocations = [...this.props.editedAllocations];
    if (Number(newAllocated.toFixed(1)) === Number(originalAllocated.toFixed(1))) {
      // If the new value is the same as original value, remove it from editedAllocations as it is not an edit if the value is the same
      newEditedAllocations = newEditedAllocations.filter(x => !(x.allocationId === editingCell.allocationId && x.month === editingCell?.month));
    } else {
      const existingIndex = newEditedAllocations.findIndex(x => x.allocationId === editingCell.allocationId && x.month === editingCell?.month);
      if (existingIndex !== -1){
        newEditedAllocations[existingIndex] = {...newEditedAllocations[existingIndex], allocated: newAllocated, grade};
      }
      else {
        newEditedAllocations = [...newEditedAllocations, {allocationId: editingCell.allocationId, month: editingCell.month, allocated: newAllocated, originalAllocated, pm, grade, customer}];
      }
    }
    this.setState({ newEditedAllocations });
  };

  handleGroupCellValueChange = (e) => {
    const { groupEditingCell } = this.state;
    this.setState({ groupEditingCell: {...groupEditingCell, value: e.target.value}});
  }

  handleCellBlur = () => {
    if (this.state.newEditedAllocations){
      this.props.onEditedAllocationsChange(this.state.newEditedAllocations);
    }
    this.setState({ editingCell: null });
  };

  getOriginalAllocated = (allocationId, month) => {
    const original = this.props.data.find(x => x.AllocationId === allocationId);
    if (!original ||!original.Months){
      return 0;
    }
    const monthObj = original.Months.find(m => m.Month === month);
    return monthObj ? Number(monthObj.Allocation) : 0;
  }

  handleGroupCellBlur = () => {
    const { groupEditingCell } = this.state;
    if (!groupEditingCell){
      this.setState({ groupEditingCell: null});
      return;
    }
    const groupRow = this.transformedDataRef[groupEditingCell.index];
    if (!groupRow ||!groupRow._leafRecords) {
      this.setState({groupEditingCell: null});
      return;
    }
    const month = groupEditingCell.month;
    const totalValue = Number(groupEditingCell.value.replace(',', '.'));

    // Calculate total original allocation from the group for the selected month
    const leafRecords = groupRow._leafRecords.filter(x => x.AllocationId != null);
    const originals = leafRecords.map(x => {
      const monthObj = x.Months?.find(m => m.Month === month);
      return {
        allocationId: x.AllocationId,
        originalValue: monthObj ? Number(monthObj.Allocation) : 0,
        pm: x.PM,
        grade: x.Grade,
        customer: x.Customer
      };
    });
    const totalOriginal = originals.reduce((sum, b) => sum + b.originalValue, 0);
    // Distribute totalValue proportionally to original value
    const editedGroupedAllocations = originals.map(b => {
      let allocated = 0;
      if (totalOriginal > 0) {
        allocated = (b.originalValue / totalOriginal) * totalValue;
      }
      return {
        allocationId: b.allocationId,
        month: month,
        allocated,
        originalAllocated: this.getOriginalAllocated(b.allocationId, month),
        pm: b.pm,
        grade: b.grade,
        customer: b.customer
      };
    });

    let newEditedAllocations = [...this.props.editedAllocations];

    editedGroupedAllocations.forEach(e => {
      if (Number(e.allocated.toFixed(1)) === Number(e.originalAllocated.toFixed(1))) {
        // If the new value is the same as original value, remove it from editedAllocations
        newEditedAllocations = newEditedAllocations.filter(x => !(x.allocationId === e.allocationId && x.month === e.month));
      } else {
        const existingIndex = newEditedAllocations.findIndex(x => x.allocationId === e.allocationId && x.month === e.month);
        if (existingIndex !== -1){
          newEditedAllocations[existingIndex] = { ...newEditedAllocations[existingIndex], ...e}
        }
        else {
          newEditedAllocations.push(e);
        }
      }
    });
    // Update state and notify allocations changed
    this.setState({ newEditedAllocations, groupEditingCell: null }, () => {
      this.props.onEditedAllocationsChange(newEditedAllocations);
    });
  }

  getAllocationValue = (allocationId, month, monthData) => {
    const edited = this.props.editedAllocations.find(x => x.allocationId === allocationId && x.month === month);
    return edited ? Number(edited.allocated) : monthData.Allocation;
  }

  handleDropdownChange = (index, value) => {
    const { selectedGroups } = this.props;
    const newSelectedGroups = [...selectedGroups];
    
    if (value === '') {
      // Remove this and all subsequent selections
      newSelectedGroups.splice(index);
    } else {
      newSelectedGroups[index] = value;
      // Remove any subsequent selections
      newSelectedGroups.splice(index + 1);
    }
    this.props.onSelectedGroupsChange(newSelectedGroups);
  };

  handleResetGrouping = () => {
    this.props.onSelectedGroupsChange([]);
    this.setState({ expanded: {}});
  };

  handleExportToExcel = () => {
    const { selectedGroups } = this.props;
    const transformedData = this.transformedDataRef;
    
    // Get detail fields (fields not in selectedGroups)
    const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);
    const allFields = [...PIVOT_FIELDS];
    const detailFields = allFields.filter(f => !selectedGroups.includes(f.key));
    
    // Create worksheet data
    const wsData = [];
    
    // Build header row
    const headerRow = [];
    
    // Add group columns to header
    selectedGroups.forEach(fieldKey => {
      const field = PIVOT_FIELDS.find(f => f.key === fieldKey);
      if (field) {
        headerRow.push(field.label);
      }
    });
    
    // Add detail field headers
    detailFields.forEach(field => {
      headerRow.push(field.label);
    });
    
    // Add month headers
    MONTH_NAMES.forEach(name => {
      headerRow.push(name);
    });
    
    wsData.push(headerRow);
    
    // Process each group row except for noallocationmatch rows
    transformedData.forEach((groupRow, groupIndex) => {
      const leafRecords = (groupRow._leafRecords || []).filter(leaf => leaf.NoAllocationMatch !== true);
      
      // Add each leaf record as a row
      leafRecords.forEach((leaf, leafIndex) => {
        const row = [];
        
        // Add group column values
        selectedGroups.forEach(fieldKey => {
          let value = groupRow[fieldKey] || '';
          row.push(this.formatFieldValue(fieldKey, value) || value);
        });
        
        // Add detail field values
        detailFields.forEach(field => {
          let value = leaf[field.key] || '';          
          row.push(this.formatFieldValue(field.key, value) || value);
        });
        
        // Add month values
        for (let m = 1; m <= 12; m++) {
          const monthData = leaf.Months?.find(x => x.Month === m) || {};
          let cellValue = this.getAllocationValue(leaf.AllocationId, m, monthData);
          row.push(typeof cellValue === 'number' ? cellValue : 0);
        }
        
        wsData.push(row);
      });
    });
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Allocation Groups');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const filename = `AllocatedVolumeDetails_${timestamp}.xlsx`;
    
    // Write file
    XLSX.writeFile(wb, filename);
  };

  getAvailableFields = (currentIndex) => {
    const { selectedGroups } = this.props;
    const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);
    return PIVOT_FIELDS.filter(field => 
      !selectedGroups.slice(0, currentIndex).includes(field.key)
    );
  };

  transformDataForMonthColumns = () => {
    const { data } = this.props;
    const { selectedGroups } = this.props;
    const editedAllocations = this.props.editedAllocations || [];
    
    if (!data || data.length === 0) return [];
    
    // If no grouping, aggregate all data by month
    if (selectedGroups.length === 0) {
      //Split data into allocations without match and allocations with match
      const noMatchData = data.filter(leaf => leaf.NoAllocationMatch);
      const matchData = data.filter(leaf => leaf.NoAllocationMatch !== true);
      const aggregate = (leaves, isNoMatch) => {
        const monthData = {};
        const seenOrderedIds = new Set();
        const seenPreliminaryIds = new Set();
        const allYearComments = [];
        
        // Aggregate months from all allocation leaves
        leaves.forEach(leaf => {
          // Collect year-level comments
          if (leaf.YearComments && leaf.YearComments.length > 0) {
            allYearComments.push(...leaf.YearComments);
          }
          
          if (leaf.Months && leaf.Months.length > 0) {
            leaf.Months.forEach(monthItem => {
              const month = monthItem.Month;
              if (!monthData[month]) {
                monthData[month] = {
                  BudgetAllocation: 0,
                  Allocation: 0,
                  Ordered: 0,
                  Preliminary: 0,
                  Comments: []
                };
              }
              monthData[month].BudgetAllocation += parseFloat(monthItem.BudgetAllocation) || 0;

              // Use edited value if present
              const edited = editedAllocations.find(x => x.allocationId === leaf.AllocationId && x.month === month);
              if(edited) {
                monthData[month].Allocation += Number(edited.allocated) || 0;
              }
              else{
                monthData[month].Allocation += parseFloat(monthItem.Allocation) || 0;
              }
              
              // Collect month-specific comments
              if (monthItem.Comments && monthItem.Comments.length > 0) {
                monthData[month].Comments.push(...monthItem.Comments);
              }
              
              // Aggregate OrderedWeeks, tracking IDs to avoid double-counting
              if (monthItem.OrderedWeeks && monthItem.OrderedWeeks.length > 0) {
                monthItem.OrderedWeeks.forEach(weekValue => {
                  if (!seenOrderedIds.has(weekValue.Id)) {
                    monthData[month].Ordered += parseFloat(weekValue.Value) || 0;
                    seenOrderedIds.add(weekValue.Id);
                  }
                });
              }
              
              // Aggregate PreliminaryWeeks, tracking IDs to avoid double-counting
              if (monthItem.PreliminaryWeeks && monthItem.PreliminaryWeeks.length > 0) {
                monthItem.PreliminaryWeeks.forEach(weekValue => {
                  if (!seenPreliminaryIds.has(weekValue.Id)) {
                    monthData[month].Preliminary += parseFloat(weekValue.Value) || 0;
                    seenPreliminaryIds.add(weekValue.Id);
                  }
                });
              }
            });
          }
        });
        
        // Create a single row with all months
        const result = {};
        result._leafRecords = leaves; // Store all leaves for sub-component
        result._yearComments = allYearComments; // Store year-level comments

        if (isNoMatch) result._noAllocationMatchGroup = true;
        
        
        let sumAllocated = 0, sumOrdered = 0, sumPreliminary = 0, sumBudget = 0;
        
        for (let m = 1; m <= 12; m++) {
          if (monthData[m]) {
            result[`Month_${m}`] = {
              allocation: monthData[m].Allocation,
              ordered: monthData[m].Ordered,
              preliminary: monthData[m].Preliminary,
              budget: monthData[m].BudgetAllocation
            };
            result[`Month_${m}_Comments`] = monthData[m].Comments || [];
            sumAllocated += monthData[m].Allocation;
            sumOrdered += monthData[m].Ordered;
            sumPreliminary += monthData[m].Preliminary;
            sumBudget += monthData[m].BudgetAllocation;
          } else {
            result[`Month_${m}`] = {
              allocation: 0,
              ordered: 0,
              preliminary: 0,
              budget: 0
            };
            result[`Month_${m}_Comments`] = [];
          }
        }
        
        result.SumAllocated = result._noAllocationMatchGroup ? '-' : this.formatNumber(sumAllocated, true);
        result.SumOrdered = this.formatNumber(sumOrdered, true);
        result.SumPreliminary = this.formatNumber(sumPreliminary, true);
        result.SumBudget = result._noAllocationMatchGroup ? '-' : this.formatNumber(sumBudget, true);
        
        return result;
      }

      const rows = [];
      if (matchData.length > 0) rows.push(aggregate(matchData, false));
      if (noMatchData.length > 0) rows.push(aggregate(noMatchData, true));
      return rows;
    }
    
    // Group data by selected fields
    const grouped = {};
    
    data.forEach(leaf => {
      // Create grouping key from the leaf's properties
      let groupKey = selectedGroups.map(field => leaf[field] || '(empty)').join('|||');
      
      // If NoAllocationMatch, append marker to group key
      if (leaf.NoAllocationMatch === true) {
        groupKey += '|||#NoAllocationMatch';
      }

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          groupValues: {},
          months: {},
          leafRecords: [],
          seenOrderedIds: new Set(),
          seenPreliminaryIds: new Set(),
          yearComments: []
        };
        selectedGroups.forEach(field => {
          grouped[groupKey].groupValues[field] = leaf[field] || '(empty)';
        });
      }
      
      // Store the leaf for detail display
      grouped[groupKey].leafRecords.push(leaf);
      
      // Collect year-level comments
      if (leaf.YearComments && leaf.YearComments.length > 0) {
        grouped[groupKey].yearComments.push(...leaf.YearComments);
      }
      
      // Aggregate months from this leaf
      if (leaf.Months && leaf.Months.length > 0) {
        leaf.Months.forEach(monthItem => {
          const month = monthItem.Month;
          if (!grouped[groupKey].months[month]) {
            grouped[groupKey].months[month] = {
              BudgetAllocation: 0,
              Allocation: 0,
              Ordered: 0,
              Preliminary: 0,
              Comments: []
            };
          }
          
          grouped[groupKey].months[month].BudgetAllocation += parseFloat(monthItem.BudgetAllocation) || 0;

          // Use edited value if present
          const edited = editedAllocations.find(
            x => x.allocationId === leaf.AllocationId && x.month === month
          );
          if (edited) {
            grouped[groupKey].months[month].Allocation += Number(edited.allocated) || 0;
          } else {
            grouped[groupKey].months[month].Allocation += parseFloat(monthItem.Allocation) || 0;
          }
          
          // Collect month-specific comments
          if (monthItem.Comments && monthItem.Comments.length > 0) {
            grouped[groupKey].months[month].Comments.push(...monthItem.Comments);
          }
          
          // Aggregate OrderedWeeks, tracking IDs to avoid double-counting
          if (monthItem.OrderedWeeks && monthItem.OrderedWeeks.length > 0) {
            monthItem.OrderedWeeks.forEach(weekValue => {
              if (!grouped[groupKey].seenOrderedIds.has(weekValue.Id)) {
                grouped[groupKey].months[month].Ordered += parseFloat(weekValue.Value) || 0;
                grouped[groupKey].seenOrderedIds.add(weekValue.Id);
              }
            });
          }
          
          // Aggregate PreliminaryWeeks, tracking IDs to avoid double-counting
          if (monthItem.PreliminaryWeeks && monthItem.PreliminaryWeeks.length > 0) {
            monthItem.PreliminaryWeeks.forEach(weekValue => {
              if (!grouped[groupKey].seenPreliminaryIds.has(weekValue.Id)) {
                grouped[groupKey].months[month].Preliminary += parseFloat(weekValue.Value) || 0;
                grouped[groupKey].seenPreliminaryIds.add(weekValue.Id);
              }
            });
          }
        });
      }
    });
    
    // Transform to array with month columns
    const resultArray = Object.values(grouped).map(group => {
      const result = { ...group.groupValues };
      result._leafRecords = group.leafRecords;
      result._yearComments = group.yearComments;

      if (group.leafRecords.length > 0 && group.leafRecords.every(leaf => leaf.NoAllocationMatch === true)) {
        result._noAllocationMatchGroup = true;
      }
      
      let sumAllocated = 0, sumOrdered = 0, sumPreliminary = 0, sumBudget = 0;
      
      for (let m = 1; m <= 12; m++) {
        if (group.months[m]) {
          result[`Month_${m}`] = {
            allocation: group.months[m].Allocation,
            ordered: group.months[m].Ordered,
            preliminary: group.months[m].Preliminary,
            budget: group.months[m].BudgetAllocation
          };
          result[`Month_${m}_Comments`] = group.months[m].Comments || [];
          sumAllocated += group.months[m].Allocation;
          sumOrdered += group.months[m].Ordered;
          sumPreliminary += group.months[m].Preliminary;
          sumBudget += group.months[m].BudgetAllocation;
        } else {
          result[`Month_${m}`] = {
            allocation: 0,
            ordered: 0,
            preliminary: 0,
            budget: 0
          };
          result[`Month_${m}_Comments`] = [];
        }
      }
      
      result.SumAllocated = result._noAllocationMatchGroup ? '-' : this.formatNumber(sumAllocated, true);
      result.SumOrdered = this.formatNumber(sumOrdered, true);
      result.SumPreliminary = this.formatNumber(sumPreliminary, true);
      result.SumBudget = result._noAllocationMatchGroup ? '-' : this.formatNumber(sumBudget, true);
      
      return result;
    });
    
    // Sort by selectedGroups fields in order, with NoAllocationMatch rows at the end
    return this.sortByFields(resultArray, selectedGroups);
  };

  formatCommentsTooltip = (comments, title) => {
    if (!comments || comments.length === 0) {
      return '';
    }
    // Filter comments so that only one comment is displayed if many with the same text were created within 3 seconds.
    // This is to reduce the amount of comments with the same text on grouped cells
    const sorted = [...comments].sort((a, b) => new Date(a.Created) - new Date(b.Created));
    const lastSeenTime = {};
    const filteredComments = sorted.filter(c => {
      const time = c.Created ? new Date(c.Created).getTime() : null;
      const key = c.Comment ?? '';
      if (time !== null && lastSeenTime[key] !== undefined && time - lastSeenTime[key] <= 3000) {
        return false;
      }
      lastSeenTime[key] = time;
      return true;
    });
    
    let html = '';
    if (title) {
      html += `<div style="font-weight: bold; margin-bottom: 5px;">${title}</div>`;
    }
    html += '<table class="commentTable">';
    html += '<thead><tr><th>Created</th><th>Created by</th><th>Customer</th><th>Comment</th></tr></thead>';
    html += '<tbody>';
    filteredComments.forEach(c => {
      const ccr = c.CustomerShortName?.CLIENT_CODE_REGIONAL ?? '';
      const shortName = c.CustomerShortName?.SHORT_NAME ?? '';
      const customerText = ccr || shortName ? `${ccr} - ${shortName}` : '';
      html += `<tr>
        <td>${c.Created || ''}</td>
        <td>${c.CreatedBy || ''}</td>
        <td>${customerText}</td>
        <td style="max-width:300px; word-break:break-word; white-space:normal;">${c.Comment || ''}</td>
      </tr>`;
    });
    html += '</tbody></table>';
    
    return html;
  };

  formatFieldValue(accessor, value) {
    if (accessor === 'AreaSalesDirector') {
      if (!this.props.areaSalesDirectors || !value) return null;
      const desc = this.props.areaSalesDirectors.find(x => x.MARKET_AREA_CODE === value)?.MARKET_DESCRIPTION ?? null;
      return formatMarketAreaDesc(value, desc);
    }
    if (accessor === 'Customer') {
      if (!this.props.customers || !value) return null;
      return this.props.customers.find(x => x.value === value)?.label ?? null;
    }
    return value;
  }

  getColumnWidth(accessor) {
    const columnWidths = {
      'AreaSalesDirector': 130,
      'Agent': 75,
      'Grade': 70,
      'Customer': 130,
      'Country': 50,
      'PM': 60,
    };
    return columnWidths[accessor] || 70;
  }

  getGroupSpacerColumnWidth() {
    const editMode = this.props.editMode;
    const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);
    const allFields = [...PIVOT_FIELDS];

    const expanderWidth = 35;
    const addButtonWidth = editMode ? 30 : 0;
    let maxWidth = 0;
    allFields.forEach(field => {
      maxWidth += this.getColumnWidth(field.key);
    });
    const spacerWidth = maxWidth - this.getDetailSpacerColumnWidth() - expanderWidth - addButtonWidth;
    if (spacerWidth > 0){
      return spacerWidth;
    }
    return 0;
  }

  getDetailSpacerColumnWidth() {
    const { selectedGroups } = this.props;
    const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);
    const allFields = [...PIVOT_FIELDS];
    
    let maxWidth = 0;
    allFields.forEach(field => {
      maxWidth += this.getColumnWidth(field.key);
    });
    let spacerWidth = maxWidth;
    const detailFields = allFields.filter(f => !selectedGroups.includes(f.key));
    detailFields.forEach(field => {
      spacerWidth -= this.getColumnWidth(field.key);
    });
    return spacerWidth;
  }

  allDetailFieldsFilled(leaf, detailFields) {
    return detailFields
      .filter(f => f.key !== 'AreaSalesDirector' && f.key !== 'Agent')
      .filter(f => this.props.legalEntity === 'NPBA' ? f.key !== 'Grade' : true)
      .every(f => this.getNewAllocationDetailFieldValue(leaf.AllocationId, f.key) !== '');
  }

  createTextColumn = (header, accessor, fieldIndex) => ({
    Header: header,
    accessor: accessor,
    Cell: (cellInfo) => {
      const value = this.formatFieldValue(accessor, cellInfo.value);
      const currentIndex = cellInfo.index;
      const data = this.transformedDataRef;
      const yearComments = cellInfo.original._yearComments || [];
      // Check if this row has NoAllocationMatch
      const hasNoAllocationMatch = cellInfo.original._leafRecords?.some(leaf => leaf.NoAllocationMatch === true);
      
      // Check if this is the first row or if the value is different from the previous row
      // Also check if all previous group fields match (so we only hide if we're in the same group)
      let shouldShow = currentIndex === 0;
      
      if (!shouldShow && currentIndex > 0 && data && data.length > currentIndex) {
        const prevRow = data[currentIndex - 1];
        const currentRow = cellInfo.original;
        
        // Check if all previous group fields match
        let allPreviousMatch = true;
        for (let i = 0; i < fieldIndex; i++) {
          const prevField = this.props.selectedGroups[i];
          if (prevRow[prevField] !== currentRow[prevField]) {
            allPreviousMatch = false;
            break;
          }
        }
        
        // If all previous fields match, check if current field is different
        if (allPreviousMatch) {
          shouldShow = prevRow[accessor] !== currentRow[accessor];
        } else {
          shouldShow = true;
        }
      }
      
      const displayValue = shouldShow ? (value != null ? value : '(empty)') : '';
      
      // Format year comments as HTML table (only show on first column)
      const yearCommentHtml = fieldIndex === 0 ? this.formatCommentsTooltip(yearComments, 'Year Allocated Volume Comments:') : '';
      const hasYearComments = fieldIndex === 0 && yearComments.length > 0;

      const showNoMatchInfo = fieldIndex === 0 && hasNoAllocationMatch;
      const noMatchInfoText = "Orders without matching allocation";
      
      return (
        <div style={{ padding: '4px', minHeight: '25px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          {showNoMatchInfo && (
            <FontAwesome 
              name="info-circle" 
              data-for="globalTip" 
              data-tip={noMatchInfoText}
              style={{ color: COLOR_NO_ALLOCATION_INFO, cursor: 'pointer' }}
            />
          )}
          <span>{displayValue}</span>
          {hasYearComments && (
            <FontAwesome 
              name="comments" 
              data-for="globalTip" 
              data-tip={yearCommentHtml}
              style={{ color: '#337ab7', cursor: 'pointer' }}
            />
          )}
        </div>
      );
    },
    width: this.getColumnWidth(accessor)
  });

  createMonthColumn = (monthNum) => {
    return {
      Header: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>{MONTH_NAMES[monthNum - 1]}</span>
          {this.state.showFullValues && (
            <FontAwesome 
            name="info-circle" 
            data-for="globalTip" 
            data-tip="Allocated (Ordered, Preliminary, Budget)"
            style={{ color: '#999', cursor: 'pointer' }}
            />
          )}
        </div>
      ),
      accessor: `Month_${monthNum}`,
      getProps: (_, rowInfo) => {
        if (!rowInfo) return {};
        
        const leafRecords = rowInfo.original._leafRecords || [];
        const allocationIds = leafRecords.map(l => l.AllocationId);
        const anyEdited = this.props.editedAllocations.some(x => allocationIds.includes(x.allocationId) && x.month === monthNum && x.allocated !== x.originalAllocated);
        const anyBudgetWarning = this.props.warningAllocations?.some(x => allocationIds.includes(Number(x.allocationId)) && x.month === monthNum && x.budgetAllocationWarning !== null);
        const isEditing = this.props.editMode && this.state.groupEditingCell
          && this.state.groupEditingCell.month === monthNum && this.state.groupEditingCell.index === rowInfo.index;

        const cellBackgroundColor = isEditing ? COLOR_EDITED : anyBudgetWarning ? COLOR_WARNING : anyEdited ? COLOR_EDITED : undefined;
        
        return {
          style: {
            backgroundColor: cellBackgroundColor
          }
        };
      },
      Cell: (cellInfo) => {
        const value = cellInfo.value;
        const comments = cellInfo.original[`Month_${monthNum}_Comments`] || [];
        const yearComments = cellInfo.original._yearComments || [];

        // Get all allocationids in the group
        const leafRecords = cellInfo.original._leafRecords ||[];
        const allocationIds = leafRecords.map(l => l.AllocationId);

        const anyEdited = this.props.editedAllocations.some(x => allocationIds.includes(x.allocationId) && x.month === monthNum && x.allocated !== x.originalAllocated);
        const anyBudgetWarning = this.props.warningAllocations?.some(x => allocationIds.includes(Number(x.allocationId)) && x.month === monthNum && x.budgetAllocationWarning !== null);
        
        // Show year comments on first month column when no groups are selected
        const showYearComments = monthNum === 1 && this.props.selectedGroups.length === 0 && yearComments.length > 0;

        // Show nomatch info-icon on first month column for rows with no allocation match when no groups are selected
        const isNoMatchGroup = cellInfo.original._noAllocationMatchGroup === true;

        // Format comments as HTML table
        let commentHtml = '';
        if (showYearComments && comments.length > 0) {
          // Show both year and month comments
          commentHtml = this.formatCommentsTooltip(yearComments, 'Year Allocated Volume Comments:') + 
                       this.formatCommentsTooltip(comments, `${MONTH_NAMES[monthNum - 1]} Allocated Volume Comments:`);
        } else if (showYearComments) {
          // Show only year comments
          commentHtml = this.formatCommentsTooltip(yearComments, 'Year Allocated Volume Comments:');
        } else if (comments.length > 0) {
          // Show only month comments
          commentHtml = this.formatCommentsTooltip(comments, `${MONTH_NAMES[monthNum - 1]} Allocated Volume Comments:`);
        }
        
        const hasComments = comments.length > 0 || showYearComments;

        const isEditing = this.props.editMode && this.state.groupEditingCell
          && this.state.groupEditingCell.month === monthNum && this.state.groupEditingCell.index === cellInfo.index;

        let displayValue = '';
        let displayValueExtra = ''; // For the (ordered|preliminary|budget) part
        let tooltipValue = '';
        // Display sum of ordered and preliminary if no match against allocation
        if (isNoMatchGroup && value) {
          displayValue = this.formatNumber(value.ordered, true);
          tooltipValue = `<div>Ordered: ${this.formatNumber(value.ordered)}</div><div>Preliminary: ${this.formatNumber(value.preliminary)}</div>`;
        } else if (value != null) {
          displayValue = this.formatNumber(value.allocation, true);
          if (this.state.showFullValues) {
            displayValueExtra = ` (${this.formatNumber(value.ordered)}|${this.formatNumber(value.preliminary)}|${this.formatNumber(value.budget)})`;
          }
          tooltipValue = `<div>Allocated: ${this.formatNumber(value.allocation)}</div><div>Ordered: ${this.formatNumber(value.ordered)}</div><div>Preliminary: ${this.formatNumber(value.preliminary)}</div><div>Budget: ${this.formatNumber(value.budget)}</div>`;

          // Calculate and display original value for the group if any allocations in the group were edited
          if (anyEdited) {
            let originalGroupTotal = 0;
            leafRecords.forEach(x => {
              // For new allocations, original is 0 as they didn't exist
              if (x.AllocationId < 0) {
                originalGroupTotal += 0;
              }
              else {
                const originalLeaf = this.props.data.find(o => o.AllocationId === x.AllocationId);
                if (originalLeaf && originalLeaf.Months) {
                  const monthData = originalLeaf.Months?.find(m => m.Month === monthNum);
                  if (monthData) {
                    originalGroupTotal += parseFloat(monthData.Allocation) || 0;
                  }
                }
              }
            });
            tooltipValue += `<div>Original: ${this.formatNumber(originalGroupTotal)}</div>`;
          }
        } else {
          displayValue = '(empty)';
          tooltipValue = '';
        }

        const cellBackgroundColor = isEditing ? COLOR_EDITED: anyBudgetWarning ? COLOR_WARNING : anyEdited ? COLOR_EDITED : undefined;
        return (
          <div 
            style={{ padding: '4px', minHeight: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            {isNoMatchGroup && monthNum === 1 && (
              <FontAwesome 
                name="info-circle" 
                data-for="globalTip" 
                data-tip="Orders without matching allocation"
                style={{ color: COLOR_NO_ALLOCATION_INFO, cursor: 'pointer' }}
              />
            )}
            {isEditing ? (
              <input
                type="text"
                value={this.state.groupEditingCell?.value ?? ''}
                autoFocus
                onChange={(e) => this.handleGroupCellValueChange(e)}
                onInput={(e) => e.target.value = e.target.value.replace(/[^0-9,]/g, '')}
                onBlur={this.handleGroupCellBlur}
                style={{ width: '60px', height: '17px', backgroundColor: cellBackgroundColor }}
              />
            ): (
              <span
                onClick={!isNoMatchGroup ? () => this.setState({groupEditingCell: { index: cellInfo.index, month: monthNum, value: (value?.allocation.toFixed(1) ?? '').replace('.', ',')}}): undefined}
                style={{ textAlign: 'left', display: 'block' }}
                data-for="globalTip"
                data-tip={tooltipValue}
              >
                <span>{displayValue}</span>
                {displayValueExtra && <span><br/>{displayValueExtra}</span>}
              </span>
            )}
            
            {hasComments && (
              <FontAwesome 
                name="comments" 
                data-for="globalTip" 
                data-tip={commentHtml}
                style={{ color: '#337ab7', cursor: 'pointer' }}
              />
            )}
          </div>
        );
      },
      width: SUM_COL_WIDTH
    };
  };

  createSumColumn = (header, accessor) => ({
    Header: header,
    accessor: accessor,
    Cell: (cellInfo) => {
      const value = cellInfo.value;
      return (
        <div style={{ padding: '4px', minHeight: '20px' }}>
          {value != null ? value : '0'}
        </div>
      );
    },
    width: SUM_COL_WIDTH
  });

  getTableColumns = () => {
    const editMode = this.props.editMode;
    const { selectedGroups } = this.props;
    const columns = [];
    const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);

    // Add + column for adding new allocations when in edit mode
    if(editMode){
      columns.push({
        Header: '',
        id: 'add-allocation',
        Cell: (cellInfo) => {
          //Dont display column on no-allocation-match groups
          const isNoMatch = cellInfo.original._noAllocationMatchGroup === true;
          if (isNoMatch){
            return null;
          }
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <Button
                bsStyle="primary"
                style={{
                  height: '24px',
                  width: '24px',
                  minWidth: '24px',
                  minHeight: '24px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  lineHeight: 1
                }}
                data-for="globalTip"
                data-tip={ "Add a new allocation to this group" }
                onClick={() => this.handleAddAllocationToGroup(cellInfo.original, cellInfo.index)}
              >
                <FontAwesome name='plus' />
              </Button>
            </div>
          );
        },
        width: 30,
      });
    }

    
    // Add group columns
    selectedGroups.forEach((fieldKey, index) => {
      const field = PIVOT_FIELDS.find(f => f.key === fieldKey);
      if (field) {
        columns.push(this.createTextColumn(field.label, field.key, index));
      }
    });
    
    // Add spacer column to align months with SubComponent
    columns.push({
      Header: '',
      id: 'spacer',
      Cell: () => null,
      width: this.getGroupSpacerColumnWidth(),
      sortable: false,
      resizable: false
    });
    
    // Add month columns (1-12)
    for (let m = 1; m <= 12; m++) {
      columns.push(this.createMonthColumn(m));
    }
    
    // Add sum columns
    columns.push(this.createSumColumn('SumAllocated', 'SumAllocated'));
    columns.push(this.createSumColumn('SumOrdered', 'SumOrdered'));
    columns.push(this.createSumColumn('SumPreliminary', 'SumPreliminary'));
    columns.push(this.createSumColumn('SumBudget', 'SumBudget'));
    
    return columns;
  };

  renderDropdowns = () => {
    const { selectedGroups } = this.props;
    const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);
    const dropdowns = [];
    
    // Show dropdowns for existing selections plus one more
    const numDropdowns = Math.min(selectedGroups.length + 1, PIVOT_FIELDS.length);
    
    for (let i = 0; i < numDropdowns; i++) {
      const availableFields = this.getAvailableFields(i);
      
      if (availableFields.length === 0) break;
      
      const options = availableFields.map(field => ({
        label: field.label,
        value: field.key
      }));
      
      const selectedValue = selectedGroups[i] ? 
        { label: PIVOT_FIELDS.find(f => f.key === selectedGroups[i])?.label, value: selectedGroups[i] } : 
        null;
      
      dropdowns.push(
        <Col sm={12} className="smaller-inputs filterGroup" key={i}>
          <FormGroup className="select">
            <InputGroup>
              <InputGroup.Addon style={{ width: "80px" }}>Group {i + 1}</InputGroup.Addon>
              <Select
                searchable={true}
                styles={this.state.dropdownStyleSmall}
                isClearable={true}
                options={options}
                value={selectedValue}
                onChange={(evt) => {
                  this.handleDropdownChange(i, evt ? evt.value : '');
                }}
              />
            </InputGroup>
          </FormGroup>
        </Col>
      );
    }
    
    return dropdowns;
  };

  // Convert capitalized field key to lowercase version used in editedAllocations
  convertFieldKeyToLowercase(fieldKey) {
    const fieldMap = {
      'PM': 'pm',
      'Grade': 'grade',
      'Country': 'country',
      'Customer': 'customer',
      'Agent': 'agent',
      'AreaSalesDirector': 'areaSalesDirector'
    };
    return fieldMap[fieldKey] || fieldKey;
  }

  getNewAllocationDetailFieldValue(allocationId, field) {
    // Find the first edited allocation for this new allocationId
    const edited = this.props.editedAllocations.find(
      x => x.allocationId === allocationId && x.month === 1
    );
    // Convert field key to lowercase version used in editedAllocations
    const lowercaseField = this.convertFieldKeyToLowercase(field);
    return edited ? edited[lowercaseField] || '' : '';
  }

  setNewAllocationDetailFieldValue(allocationId, field, value) {
    // Convert field key to lowercase version used in editedAllocations
    const lowercaseField = this.convertFieldKeyToLowercase(field);
    // Update all 12 months for this allocationId in editedAllocations
    const newEditedAllocations = this.props.editedAllocations.map(x => {
      if (x.allocationId === allocationId) {
        return { ...x, [lowercaseField]: value };
      }
      return x;
    });
    this.props.onEditedAllocationsChange(newEditedAllocations);
  }

  validateAllocationInput = async (input, column) => {
    const { API_BASE_URL } = constants;
    try {
      await axios.get(
        `${API_BASE_URL}Allocation/ValidateAllocationInput`,
        {
          headers: this.state.header,
          params: { input, column }
        }
      );
      return { isValid: true, error: null };
    } catch (error) {
      const errorMessage = error.response?.data?.Message || error.response?.data || `Invalid input for ${column}`;
      return { isValid: false, error: errorMessage };
    }
  };

  renderDetailsField = (leaf, field, isNewAllocation) => {
    const editableLeaf = isNewAllocation && this.props.editMode;
    const editableField = editableLeaf && field.key !== 'AreaSalesDirector' && field.key !== 'Agent';
    const isFieldSelected = this.state.editingCell && this.state.editingCell.allocationId === leaf.AllocationId && this.state.editingCell.fieldKey === field.key;

    if (editableField) {
      if (isFieldSelected) {
        // Editable field and selected
        return {
          content: (
            <input
              style={{ width: '60px', height: '17px', backgroundColor: COLOR_EDITED }}
              value={this.state.editingCell?.value ?? ''}
              autoFocus
              onChange={e =>
                this.setState({
                  editingCell: { ...this.state.editingCell, value: e.target.value },
                })
              }
              onBlur={e => {
                const inputValue = e.target.value;
                this.setNewAllocationDetailFieldValue(
                  leaf.AllocationId,
                  field.key,
                  inputValue
                );
                this.setState({ editingCell: null });
                if (inputValue) {
                  // Validate input against backend
                  this.validateAllocationInput(inputValue, field.key).then(({ isValid, error }) => {
                    if (!isValid) {
                      this.setState(prevState => ({
                        fieldValidationErrors: {
                          ...prevState.fieldValidationErrors,
                          [`${leaf.AllocationId}_${field.key}`]: error
                        },
                      }));
                    }
                    else {
                      // No error, clear any existing error for this field
                      this.setState(prevState => {
                        const newErrors = { ...prevState.fieldValidationErrors };
                        delete newErrors[`${leaf.AllocationId}_${field.key}`];
                        return { fieldValidationErrors: newErrors };
                      });
                    }
                  });
                }
              }}
            />
          ),
          backgroundColor: COLOR_EDITED
        };
      }
      else {
        //Editable field but not selected
        const fieldValue = this.getNewAllocationDetailFieldValue(leaf.AllocationId, field.key);
        const fieldValidationError = this.state.fieldValidationErrors?.[`${leaf.AllocationId}_${field.key}`];
        const backgroundColor = fieldValidationError || !fieldValue ? COLOR_WARNING : COLOR_EDITED;
        return {
          content: (
            <span
              style= {{
                cursor: 'pointer',
                width: '100%',
                height: '100%',
                display: 'block'
              }}
              onClick={() => {
                this.setState({
                  editingCell: {
                    allocationId: leaf.AllocationId,
                    fieldKey: field.key,
                    value: fieldValue,
                  },
                });
                }
              }
            >
              {fieldValue || '\u00A0'}
              {fieldValidationError && 
                <FontAwesome
                  name='info-circle'
                  data-for='globalTip'
                  data-tip={fieldValidationError}
                  style={{ color: '#ffffff', cursor: 'pointer', marginLeft: '5px' }}
                />
              }
            </span>
          ),
          backgroundColor: backgroundColor
        };
      }
    }
    else {
      // Non editable field
      return {
        content: (
          <span>
            {this.formatFieldValue(field.key, leaf[field.key])}
          </span>
        ),
        backgroundColor: undefined
      };
    }
  }

  componentDidUpdate() {
    // Delay rebuild to ensure SubComponent tooltips are registered
    setTimeout(() => ReactTooltip.rebuild(), 100);
  }

  render() {
    const transformedData = this.transformDataForMonthColumns();
    this.transformedDataRef = transformedData;
    const infoGrouped = `
      <div>
          <strong>Allocated volume details</strong><br/>
          View and edit allocated volume by different groupings<br/><br/>

          <strong>Grouping:</strong><br/>
          • Select several grouping levels from available dimensions<br/>
          • Click "Reset Groups" to clear all groupings<br/><br/>

          <strong>Viewing Data:</strong><br/>
          • Expand groups (▶) to see detailed allocation rows<br/>
          • Hover over month cells to see:<br/>
          <div style="padding-left: 20px;">
              • <strong>Allocated:</strong> Current allocated volume for the month<br/>
              • <strong>Ordered:</strong> Actual order volume matching the booking<br/>
              • <strong>Preliminary:</strong> Actual preliminary order volume matching the booking<br/>
              • <strong>Budget:</strong> Budget allocated volume<br/>
          </div>
          • Sum columns show yearly totals for Allocated, Ordered, Preliminary, and Budget<br/>
          • Comment icons indicate allocated volume comments - hover to view<br/>
          • Info icons (i) provide additional context and warnings - hover to view<br/>
          • Orange background rows indicate orders without matching allocations<br/><br/>

          <strong>Editing Allocations:</strong><br/>
          • Click the pencil icon to enter edit mode<br/>
          • Two editing approaches available:<br/>
          <div style="padding-left: 20px;">
              <strong>Group-level editing:</strong><br/>
              • Click any month value in a group row<br/>
              • Enter new total allocated volume<br/>
              • Value is distributed proportionally to each row within the group based on each row's budget allocation<br/><br/>
              
              <strong>Row-level editing:</strong><br/>
              • Expand the group to view detail rows<br/>
              • Click individual month cells to edit specific allocations<br/>
              • Enter exact allocated volume for that allocation<br/>
          </div>
          • Yellow background indicates edited cells<br/>
          • Red background indicates validation warnings<br/><br/>
          • Click "Save" to commit changes<br/>
          
          <strong>Adding New Allocations:</strong><br/>
          • In edit mode, click the + button on any group<br/>
          • Fill in required fields (PM, Customer, Country, etc.)<br/>
          • Once all fields are filled, edit month values as needed<br/></br>
          
          <strong>Export:</strong><br/>
          • Click "Excel" button to export current view<br/>
          • Exports grouped data with all detail rows<br/>
          • Excludes "no allocation match" rows from export<br/>
      </div>`;
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <legend className="allocationLegend" style={{ display: 'inline-block', width: 'auto', paddingBottom: 0 }}><Convert.TextTo value_metric={"Details market area (t)"} value_imperial={"Details market area (tn)"}/></legend>
          <FontAwesome 
              name="info-circle" 
              data-for="globalTip" 
              data-tip={infoGrouped}
              style={{ cursor: 'pointer', fontSize: '16px' }}
          />
        </div>
        <Dialog ref={el => { this.dialog = el; }} />
        <ReactTooltip id="globalTip" html place="bottom" effect="solid"/>
        <Form inline className="searchForm" autoComplete="off">
          <Row style={{ width: '100%'}}>
            <Col sm={6} style={{ width: '70%'}}>
              <FormGroup>
              {this.renderDropdowns()}
                <Button
                  bsStyle="primary"
                  onClick={this.handleResetGrouping}
                  style={{ marginLeft: '10px' }}
                >
                  Reset Groups
                </Button>
              </FormGroup>
            </Col>
            <Col sm={6} style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              <FormGroup>
                {!this.props.editMode &&
                  <Button
                    bsStyle="success"
                    onClick={this.handleExportToExcel}
                    style={{ marginRight: '10px' }}
                  >
                    <FontAwesome name="file-excel-o" style={{ marginRight: '5px' }} />
                    Excel Allocated Volume
                  </Button>
                }
                <Button
                  bsStyle="primary"
                  onClick={() => this.setState({ showFullValues: !this.state.showFullValues })}
                >
                  {this.state.showFullValues ? 'Show Only Allocated Volume' : 'Show All Values'}
                </Button>
                {this.props.editMode &&
                  <Button
                    bsStyle={"primary"}
                    onClick={this.handleSaveButtonClick}
                    style={{ marginLeft: '10px', width: '100px' }}
                    disabled={!this.props.editedAllocations || this.props.editedAllocations.length === 0 || this.props.verifyingAllocationCount > 0}
                    data-tip={this.props.verifyingAllocationCount > 0 ? "Please wait, verifying changes.." : null}
                    data-for={"globalTip"}
                  >
                    Save
                    {this.props.verifyingAllocationCount > 0 &&
                      <FontAwesome
                        name="spinner"
                        spin
                        data-tip={"Please wait, verifying changes.."}
                        data-for={"globalTip"}
                      />
                    }
                  </Button>
                }
                {this.props.editMode ?
                  <Button
                    bsStyle={"primary"}
                    onClick={this.handleCancelButtonClick}
                    style={{ marginLeft: '10px', width: '100px' }}
                  >
                      Cancel
                  </Button> : 
                  (hasGrant(Grants.TYPES.CustomerProfile_EditCustomerAllocation) &&
                    <Button
                      bsStyle={"primary"}
                      onClick={this.handleEditButtonClick}
                      style={{ marginLeft: '10px', width: '100px' }}
                    >
                      <FontAwesome
                        name={"pencil"}
                      />
                    </Button>
                  )
                }
              </FormGroup>
            </Col>
          </Row>
        </Form>

        <NPReactTable
          data={transformedData}
          columns={this.getTableColumns()}
          expanded={this.state.expanded}
          defaultPageSize={13}
          className="-striped -highlight allocation-groups-table"
          headerZIndex={1}
          onExpandedChange={expanded => {
            // Rebuild tooltips when rows are expanded/collapsed
            this.setState({ expanded});
            setTimeout(() => ReactTooltip.rebuild(), 20);
          }}
          onPageChange={() => setTimeout(() => ReactTooltip.rebuild(), 20)}
          getTrProps={(state, rowInfo) => {
            if (rowInfo && rowInfo.original) {
              // Check if any leaf record in this row has NoAllocationMatch = true
              const hasNoAllocationMatch = rowInfo.original._leafRecords?.some(leaf => leaf.NoAllocationMatch === true);
              if (hasNoAllocationMatch) {
                const hoverColor = darkenColor(COLOR_NO_MATCH, 20);
                return {
                  style: {
                    backgroundColor: COLOR_NO_MATCH,
                    color: '#000000',
                    cursor: 'pointer'
                  },
                  onMouseEnter: (e) => {
                    e.currentTarget.style.backgroundColor = hoverColor;
                  },
                  onMouseLeave: (e) => {
                    e.currentTarget.style.backgroundColor = COLOR_NO_MATCH;
                  }
                };
              }
              
              // Apply alternating group colors based on first grouping field
              const firstGroupField = this.props.selectedGroups[0];
              if (firstGroupField) {
                const currentValue = rowInfo.original[firstGroupField];
                
                // Determine which group this belongs to and row position within group
                let groupIndex = 0;
                let rowInGroupIndex = 0;
                let isFirstInGroup = false;
                let isLastInGroup = false;
                
                for (let i = 0; i <= rowInfo.index; i++) {
                  const row = transformedData[i];
                  const rowValue = row[firstGroupField];
                  
                  if (i === 0 || transformedData[i - 1][firstGroupField] !== rowValue) {
                    // New group starts
                    if (i > 0) groupIndex++;
                    rowInGroupIndex = 0;
                  } else {
                    rowInGroupIndex++;
                  }
                }
                
                // Check if this is the first row in the group
                isFirstInGroup = rowInfo.index === 0 || transformedData[rowInfo.index - 1][firstGroupField] !== currentValue;
                
                // Check if this is the last row in the group
                isLastInGroup = rowInfo.index === transformedData.length - 1 || transformedData[rowInfo.index + 1][firstGroupField] !== currentValue;
                
                // Define color pairs for different groups (light and dark shades)
                const colorPairs = [
                  { light: '#f0f7ff', dark: '#e1f0ff', border: '#e1f0ff' }, // Soft blue with medium blue border
                  { light: '#fafafa', dark: '#eeeeee', border: '#eeeeee' }  // Soft grey with medium grey border
                ];
                
                // Select color pair based on group index
                const colorPair = colorPairs[groupIndex % colorPairs.length];
                
                // Alternate between light and dark within the group
                const backgroundColor = rowInGroupIndex % 2 === 0 ? colorPair.light : colorPair.dark;
                
                // Build border style for group outline
                const borderStyle = `2px solid ${colorPair.border}`;
                const style = {
                  backgroundColor: backgroundColor,
                  borderLeft: borderStyle,
                  borderRight: borderStyle,
                  cursor: 'pointer'
                };
                
                if (isFirstInGroup) {
                  style.borderTop = borderStyle;
                }
                
                if (isLastInGroup) {
                  style.borderBottom = borderStyle;
                }
                
                return { 
                  style,
                  onMouseEnter: (e) => {
                    e.currentTarget.style.backgroundColor = darkenColor(backgroundColor);
                  },
                  onMouseLeave: (e) => {
                    e.currentTarget.style.backgroundColor = backgroundColor;
                  }
                };
              }
            }
            return {};
          }}
          SubComponent={(row) => {
            const leafRecords = row.original._leafRecords || [];
            const { selectedGroups } = this.props;
            const PIVOT_FIELDS = getPivotFields(this.props.legalEntity);

            // Get all fields that are not in the selectedGroups list
            const allFields = [...PIVOT_FIELDS];
            const detailFields = allFields.filter(f => !selectedGroups.includes(f.key));
            
            // Sort leaf records by detail fields using the same custom sort logic
            const sortedLeafRecords = this.sortByFields(leafRecords, detailFields);
            
            const detailSpacerWidth = this.getDetailSpacerColumnWidth();
            
            return (
              <div style={{ backgroundColor: '#f8f8f8' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      {detailFields.map(field => (
                        <th key={field.key} style={{ padding: '4px', textAlign: 'left', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: '#fff', width: `${this.getColumnWidth(field.key)}px`, position: 'sticky', top: 0, zIndex: 4 }}>
                          {field.label}
                        </th>
                      ))}
                      <th
                        style={{
                          width: `${detailSpacerWidth}px`,
                          minWidth: `${detailSpacerWidth}px`,
                          maxWidth: `${detailSpacerWidth}px`,
                        }}
                      />
                      {MONTH_NAMES.map((monthName, idx) => {
                        return (
                          <th key={idx} style={{ padding: '8px', textAlign: 'left', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 4, width: `${SUM_COL_WIDTH}px` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span>{monthName}</span>
                              <FontAwesome 
                                name="info-circle" 
                                data-for="globalTip" 
                                data-tip="Allocated (Ordered, Preliminary, Budget)"
                                style={{ color: '#999', cursor: 'pointer' }}
                              />
                            </div>
                          </th>
                        );
                      })}
                      <th style={{ padding: '8px', textAlign: 'left', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 4, width: SUM_COL_WIDTH }}>SumAllocated</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 4, width: SUM_COL_WIDTH }}>SumOrdered</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 4, width: SUM_COL_WIDTH }}>SumPreliminary</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 4 }}>SumBudget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeafRecords.map((leaf, idx) => {
                      let sumAllocated = 0, sumOrdered = 0, sumPreliminary = 0, sumBudget = 0;
                      const hasYearComments = leaf.YearComments && leaf.YearComments.length > 0;
                      const yearCommentHtml = this.formatCommentsTooltip(leaf.YearComments, 'Year Allocated Volume Comments:');
                      const isNewAllocation = leaf.AllocationId < 0;
                      const rowBgColor = leaf.NoAllocationMatch === true ? COLOR_NO_MATCH : '#ffffff';
                      return (
                      <tr key={idx} style={{ borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', backgroundColor: rowBgColor, color: leaf.NoAllocationMatch ? '#000000' : 'inherit', cursor: 'pointer' }} 
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkenColor(rowBgColor)}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowBgColor}
                      >
                        {detailFields.map((field, fieldIdx) => {
                          const {content, backgroundColor} = this.renderDetailsField(leaf, field, isNewAllocation);
                          return (
                            <td key={field.key} style={{ padding: '4px', backgroundColor: backgroundColor, width: `${this.getColumnWidth(field.key)}px`, borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                {fieldIdx === 0 && leaf.NoAllocationMatch === true && (
                                  <FontAwesome 
                                    name="info-circle" 
                                    data-for="globalTip" 
                                    data-tip="Orders without matching allocation"
                                    style={{ color: COLOR_NO_ALLOCATION_INFO, cursor: 'pointer' }}
                                  />
                                )}
                                {content}
                                {fieldIdx === 0 && hasYearComments && (
                                  <FontAwesome 
                                    name="comments" 
                                    data-for="globalTip" 
                                    data-tip={yearCommentHtml}
                                    style={{ color: '#337ab7', cursor: 'pointer' }}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td
                        style={{
                          width: `${detailSpacerWidth}px`,
                          minWidth: `${detailSpacerWidth}px`,
                          maxWidth: `${detailSpacerWidth}px`,
                        }}
                      />
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
                          const monthData = leaf.Months.find(x => x.Month === m) || {};
                          const allocationId = leaf.AllocationId;
                          const pm = leaf.PM ?? row.original.PM;
                          const grade = leaf.Grade ?? row.original.Grade;
                          const customer = leaf.Customer ?? row.original.Customer;
                          const originalAllocated = monthData.Allocation;
                          const allocationValue = this.getAllocationValue(allocationId, m, monthData);
                          const isNoMatch = leaf.NoAllocationMatch === true;

                          const monthOrdered = monthData.OrderedWeeks.reduce((sum, w) => sum + (parseFloat(w.Value) || 0), 0);
                          const monthPreliminary = monthData.PreliminaryWeeks.reduce((sum, w) => sum + (parseFloat(w.Value) || 0), 0);

                          sumAllocated += allocationValue || 0;
                          sumBudget += monthData.BudgetAllocation || 0;
                          sumOrdered += monthOrdered || 0;
                          sumPreliminary += monthPreliminary || 0;

                          let cellValue = '';
                          let tooltipValue = '';

                          if (isNoMatch && monthData) {
                            const sumOrderedPreliminary = (monthOrdered || 0) + (monthPreliminary || 0);
                            cellValue = this.formatNumber(sumOrderedPreliminary, true);
                            tooltipValue = `<div>Ordered: ${this.formatNumber(monthOrdered)}</div><div>Preliminary: ${this.formatNumber(monthPreliminary)}</div>`;
                          } else if (monthData) {
                            cellValue = this.state.showFullValues
                              ? `${this.formatNumber(allocationValue, true)} (${this.formatNumber(monthOrdered)}|${this.formatNumber(monthPreliminary)}|${this.formatNumber(monthData.BudgetAllocation)})`
                              : this.formatNumber(allocationValue, true);
                            tooltipValue = `<div>Allocated: ${this.formatNumber(allocationValue)}</div><div>Ordered: ${this.formatNumber(monthOrdered)}<div/><div>Preliminary: ${this.formatNumber(monthPreliminary)}<div/><div>Budget: ${this.formatNumber(monthData.BudgetAllocation)}</div>`;
                          } else {
                            cellValue = '0';
                            tooltipValue = '';
                          }
                          
                          // Prepare month comment HTML
                          const monthComments = monthData?.Comments || [];
                          const monthCommentHtml = this.formatCommentsTooltip(monthComments, `${MONTH_NAMES[m - 1]} Allocated Volume Comments:`);
                          const hasMonthComments = monthComments.length > 0;
                          
                          const isEditing = this.props.editMode && this.state.editingCell && this.state.editingCell.allocationId === allocationId &&
                            this.state.editingCell.month === m;
                          
                          const isEdited = this.props.editedAllocations.some(x => x.allocationId === allocationId && x.month === m && x.allocated !== x.originalAllocated);
                          if (isEdited) {
                            tooltipValue += `<div>Original: ${this.formatNumber(originalAllocated)}</div>`;
                          }
                          const budgetAllocationWarningObj = this.props.warningAllocations?.find(x => Number(x.allocationId) === allocationId && x.month === m && x.budgetAllocationWarning !== null);
                          const hasBudgetAllocationWarning = !!budgetAllocationWarningObj;
                          const cellBackgroundColor = isEditing ? COLOR_EDITED: hasBudgetAllocationWarning ? COLOR_WARNING : isEdited ? COLOR_EDITED : undefined;
                          
                          // Used to prevent month value editing on new allocations until all detail-fields are entered (ex PM, Customer...)
                          const canEditMonth = !isNewAllocation || this.allDetailFieldsFilled(leaf, detailFields);
                          return (
                            <td key={m} style={{ width: this.state.showFullValues ? '280px' : '100px', backgroundColor: cellBackgroundColor, borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={this.state.editingCell?.value ?? ''}
                                    autoFocus
                                    onChange={(e) => this.handleCellValueChange(e, originalAllocated, pm, grade, customer)}
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9,]/g, '')}
                                    onBlur={this.handleCellBlur}
                                    style={{ width: '60px', height: '17px', backgroundColor: cellBackgroundColor }}
                                  />
                                ) : (
                                  <span
                                    onClick={canEditMonth ? () => this.setState({ editingCell: { allocationId, month: m, value: allocationValue.toFixed(1).replace('.', ',')} }) : undefined}
                                    style={{ textAlign: 'left', display: 'block' }}
                                    data-for="globalTip"
                                    data-tip={canEditMonth ? tooltipValue : "Fill all fields before editing months"}
                                  >
                                    {cellValue}
                                  </span>
                                )}
                                {hasBudgetAllocationWarning && this.props.verifyingAllocationCount === 0 && (
                                  <FontAwesome 
                                    name="info-circle" 
                                    data-for="globalTip" 
                                    data-tip={
                                      `<div>${getBudgetWarningText(budgetAllocationWarningObj, this.props.areaSalesDirectors)}</div>`
                                    }
                                    style={{ color: '#ffffff', cursor: 'pointer' }}
                                  />
                                )}
                                {hasMonthComments && (
                                  <FontAwesome 
                                    name="comments" 
                                    data-for="globalTip" 
                                    data-tip={monthCommentHtml}
                                    style={{ color: '#337ab7', cursor: 'pointer' }}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ padding: '4px', width: `${SUM_COL_WIDTH}px`, borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>{leaf.NoAllocationMatch ? '-' : this.formatNumber(sumAllocated, true)}</td>
                        <td style={{ padding: '4px', width: `${SUM_COL_WIDTH}px`, borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>{this.formatNumber(sumOrdered, true)}</td>
                        <td style={{ padding: '4px', width: `${SUM_COL_WIDTH}px`, borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>{this.formatNumber(sumPreliminary, true)}</td>
                        <td style={{ padding: '4px', width: `${SUM_COL_WIDTH}px`, borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>{leaf.NoAllocationMatch ? '-' : this.formatNumber(sumBudget, true)}</td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            );
          }}
          style={{
            height: '600px'
          }}
        />
      </>
    );
  }
}

export default AllocationGroups;
